import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../../utils/api';
import { api_url } from '../../../config';
import { VscSend } from "react-icons/vsc";
import { ColorRing } from 'react-loader-spinner';
import { hasPermission } from '../../../Redux/permissionSlice';
import { FaComments, FaUserCircle } from 'react-icons/fa';

const OrderComments = ({ order, orderId, roles, onLoadingChange }) => {
    const [loading, setLoading] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [comments, setComments] = useState(null);
    const [newComment, setNewComment] = useState('');
    const { id, token, branch_id } = useSelector((state) => state.auth);
    const messagesEndRef = useRef(null);

    const fetchComments = async () => {
        if (!navigator.onLine) {
            toast("You're offline");
            setLoading(false);
            onLoadingChange?.(false);
            return;
        }

        try {
            setLoading(true);
            onLoadingChange?.(true);
            const response = await api.get(api_url + `/api/v1/retailer/user/${id}/branch/${branch_id}/order/${orderId}/comments`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
            });

            const { data } = response.data;
            setComments(data);
        } catch (error) {
            console.log(error);
            toast("Order comments unavailable");
            setComments([]);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    useEffect(() => {
        if (orderId && comments === null) {
        fetchComments();
        }
    }, [orderId]);

    useEffect(() => {
        if (!loading && comments) {
        scrollToBottom();
        }
    }, [comments, loading]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleSendComment = async () => {
        if (!newComment.trim()) return;
        setBtnLoader(true);
        try {
            await api.post(api_url + `/api/v1/retailer/user/${id}/branch/${branch_id}/order/${orderId}/comments`,
                {
                    content: newComment
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                });
            fetchComments();
            setNewComment('');
        } catch (error) {
            console.log(error);
            toast("Failed to send comment");
        } finally {
            setBtnLoader(false);
        }
    };

    const ChatMessage = ({ comment }) => {
        const isRetail = comment?.model === 'Retail';
        return (
            <div className={`flex flex-col ${isRetail ? 'items-end' : 'items-start'} mb-4`}>
                <div className={`max-w-[80%] p-4 rounded-2xl shadow-sm ${isRetail
                    ? 'bg-green-50 border border-green-100'
                    : 'bg-gray-50 border border-gray-100'
                    }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <FaUserCircle className={`text-lg ${isRetail ? 'text-green-500' : 'text-gray-500'}`} />
                        <span className="text-sm font-medium">
                            {comment?.user?.first_name} {comment?.user?.last_name}
                        </span>
                    </div>
                    <div className="mb-2 text-sm text-gray-700">{comment?.content}</div>
                    <div className="text-xs text-gray-400">
                        {new Date(comment?.created_at).toLocaleString('en-GB')}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <ColorRing height="40" width="40" colors={['#10B981', '#10B981', '#10B981', '#10B981', '#10B981']} />
                    </div>
                ) : !comments || comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <FaComments className="mb-2 text-4xl" />
                        <p>No comments yet</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {comments.map(comment => (
                            <ChatMessage key={comment.id} comment={comment} />
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Input Area */}
            {hasPermission(roles, ['orderWriter', 'orderAdmin']) && !loading && (
                <div className="p-3 bg-white border-t">
                    <div className="flex gap-2">
                        <textarea
                            className="flex-1 p-2 text-sm border border-gray-200 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent cursor-text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Type your comment..."
                            rows="2"
                        />
                        <button
                            className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-1 focus:ring-green-500 focus:ring-offset-1 transition-colors flex items-center gap-1.5 text-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-500 disabled:hover:bg-gray-300"
                            onClick={handleSendComment}
                            disabled={btnLoader || !newComment.trim()}
                        >
                            {btnLoader ? (
                                <ColorRing height="16" width="16" colors={['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff']} />
                            ) : (
                                <>
                                    <VscSend size={14} />
                                    <span>Send</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderComments;